import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    students: 'Élèves',
    teachers: 'Enseignants',
    staff: 'Personnel',
    payments: 'Paiements',
    reports: 'Rapports',
    bulletins: 'Bulletins',
    expenses: 'Dépenses',
    supplies: 'Fournitures',
    attendance: 'Présences',
    salaries: 'Salaires',
    settings: 'Paramètres',
    users: 'Utilisateurs',
    logout: 'Déconnexion',
    
    // Auth
    login: 'Connexion',
    email: 'Email',
    password: 'Mot de passe',
    loginButton: 'Se connecter',
    forgotPassword: 'Mot de passe oublié ?',
    
    // Dashboard
    welcome: 'Bienvenue',
    totalStudents: 'Total Élèves',
    totalTeachers: 'Total Enseignants',
    totalStaff: 'Personnel',
    todayPayments: 'Paiements du jour',
    monthPayments: 'Paiements du mois',
    outstandingBalance: 'Solde impayé',
    
    // Students
    addStudent: 'Ajouter un élève',
    editStudent: 'Modifier l\'élève',
    firstName: 'Prénom',
    lastName: 'Nom',
    fatherName: 'Nom du père',
    motherName: 'Nom de la mère',
    parentContact: 'Contact parent',
    nationality: 'Nationalité',
    address: 'Adresse',
    class: 'Classe',
    cycle: 'Cycle',
    dateOfBirth: 'Date de naissance',
    importCSV: 'Importer CSV',
    exportExcel: 'Exporter Excel',
    search: 'Rechercher',
    actions: 'Actions',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    
    // Payments
    addPayment: 'Nouveau paiement',
    paymentType: 'Type de paiement',
    tuition: 'Scolarité',
    canteen: 'Cantine',
    transport: 'Transport',
    extra: 'Extra',
    amount: 'Montant',
    paymentMethod: 'Mode de paiement',
    cash: 'Espèces',
    cheque: 'Chèque',
    transfer: 'Virement',
    deposit: 'Dépôt',
    receipt: 'Reçu',
    printReceipt: 'Imprimer reçu',
    balance: 'Solde',
    
    // Reports
    financialReport: 'Rapport financier',
    forecastReport: 'Prévisions',
    actualReport: 'Réel',
    totalExpected: 'Total attendu',
    totalPaid: 'Total payé',
    totalBalance: 'Solde total',
    
    // Bulletins
    addBulletin: 'Nouveau bulletin',
    period: 'Période',
    trimester1: '1er Trimestre',
    trimester2: '2ème Trimestre',
    trimester3: '3ème Trimestre',
    semester1: '1er Semestre',
    semester2: '2ème Semestre',
    schoolYear: 'Année scolaire',
    subject: 'Matière',
    grade: 'Note',
    average: 'Moyenne',
    teacherComment: 'Appréciation',
    rank: 'Rang',
    downloadPDF: 'Télécharger PDF',
    
    // Staff
    addTeacher: 'Ajouter enseignant',
    addStaff: 'Ajouter personnel',
    contact: 'Contact',
    salary: 'Salaire',
    canteenStaff: 'Personnel cantine',
    transportStaff: 'Personnel transport',
    educators: 'Éducateurs',
    cleaningStaff: 'Agents de ménage',
    directionStaff: 'Direction',
    pedagogicalGroup: 'Pédagogique',
    servicesGroup: 'Services',
    directionGroup: 'Direction',
    function: 'Fonction',
    contractType: 'Type de contrat',
    address: 'Adresse',
    birthdate: 'Date de naissance',
    hireDate: 'Date d\'embauche',
    contractCDI: 'CDI',
    contractCDD: 'CDD',
    contractIntern: 'Stagiaire',
    contractVacataire: 'Vacataire',
    contractOther: 'Autre',
    
    // Attendance
    markAttendance: 'Marquer présence',
    present: 'Présent',
    absent: 'Absent',
    late: 'Retard',
    date: 'Date',
    
    // Expenses
    addExpense: 'Ajouter dépense',
    designation: 'Désignation',
    schoolExpense: 'Dépense scolaire',
    canteenExpense: 'Dépense cantine',
    transportExpense: 'Dépense transport',
    
    // Supplies
    addSupply: 'Ajouter fourniture',
    quantity: 'Quantité',
    unitPriceSale: 'Prix vente unitaire',
    unitPricePurchase: 'Prix achat unitaire',
    total: 'Total',
    
    // Users
    addUser: 'Ajouter utilisateur',
    role: 'Rôle',
    admin: 'Administrateur',
    director_general: 'Directeur Général',
    comptable: 'Comptable',
    rh: 'Ressources Humaines',
    director_studies: 'Directeur des études',
    cashier: 'Caissier',
    secretary: 'Secrétariat',
    vigil: 'Vigil',
    user: 'Utilisateur',
    
    // Common
    confirm: 'Confirmer',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer ?',
    success: 'Succès',
    error: 'Erreur',
    loading: 'Chargement...',
    noData: 'Aucune donnée',
    all: 'Tous',
    filter: 'Filtrer',
    
    // Cycles
    maternelle: 'Maternelle',
    primaire: 'Primaire',
    college: 'Collège',
    lycee: 'Lycée',
    
    // Months
    january: 'Janvier',
    february: 'Février',
    march: 'Mars',
    april: 'Avril',
    may: 'Mai',
    june: 'Juin',
    july: 'Juillet',
    august: 'Août',
    september: 'Septembre',
    october: 'Octobre',
    november: 'Novembre',
    december: 'Décembre',
    
    // Parent Portal
    parentPortal: 'Portail Parent',
    myChildren: 'Mes enfants',
    childBulletins: 'Bulletins',
    paymentHistory: 'Historique des paiements',
    messages: 'Messages',
    sendMessage: 'Envoyer un message',
    newMessage: 'Nouveau message',
    inbox: 'Boîte de réception',
    subject: 'Objet',
    message: 'Message',
    reply: 'Répondre',
    viewDetails: 'Voir détails',
    parentRegister: 'Inscription Parent',
    studentCode: 'Code élève',
    registerAsParent: 'S\'inscrire comme parent',
    alreadyHaveAccount: 'Déjà un compte ?',
    noAccount: 'Pas encore de compte ?',
    totalDue: 'Total dû',
    amountPaid: 'Montant payé',
    remainingBalance: 'Solde restant',
    phone: 'Téléphone',
    generateCode: 'Générer code parent',
    parentCode: 'Code parent',
    copyCode: 'Copier le code',
    codeCopied: 'Code copié!',
    messageSchool: 'Contacter l\'école',
    unread: 'Non lu',
    read: 'Lu',
    replied: 'Répondu',
    from: 'De',
    to: 'À',
    sentAt: 'Envoyé le',

    // School Settings & Appointments
    schoolSettings: 'Paramètres de l\'école',
    generalInfo: 'Informations générales',
    schoolYears: 'Années scolaires',
    feeConfig: 'Configuration des frais',
    classFeeConfig: 'Frais par classe',
    schoolName: 'Nom de l\'école',
    founder: 'Fondateur',
    directorOfStudies: 'Directeur des études',
    logoUrl: 'URL du logo',
    addSchoolYear: 'Ajouter une année',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    active: 'Active',
    setActive: 'Activer',
    closeYear: 'Clôturer',
    closedYear: 'Clôturée',
    fiscalArchives: 'Archives comptables',
    registrationFee: 'Frais d\'inscription',
    reregistrationFee: 'Frais de réinscription',
    transportZones: 'Zones de transport',
    addZone: 'Ajouter une zone',
    zoneName: 'Nom de la zone',
    zoneAmount: 'Montant',
    appointments: 'Rendez-vous',
    bookAppointment: 'Prendre rendez-vous',
    teacher: 'Enseignant',
    selectTeacher: 'Sélectionner un enseignant',
    selectStudent: 'Sélectionner un élève',
    selectDate: 'Choisir une date',
    selectSlot: 'Choisir un créneau',
    reason: 'Motif',
    pending: 'En attente',
    confirmed: 'Confirmé',
    cancelled: 'Annulé',
    completed: 'Terminé',
    confirmAppointment: 'Confirmer',
    cancelAppointment: 'Annuler le RDV',
    completeAppointment: 'Marquer terminé',
    setAvailability: 'Définir disponibilité',
    myAvailability: 'Mes disponibilités',
    dayMonday: 'Lundi',
    dayTuesday: 'Mardi',
    dayWednesday: 'Mercredi',
    dayThursday: 'Jeudi',
    dayFriday: 'Vendredi',
    daySaturday: 'Samedi',
    daySunday: 'Dimanche',
    startTime: 'Heure de début',
    endTime: 'Heure de fin',
    slotDuration: 'Durée (min)',
    noSlots: 'Aucun créneau disponible',
    appointmentBooked: 'Rendez-vous réservé',
    saveSettings: 'Enregistrer',

    // Timetable
    timetables: 'Emplois du temps',
    addSlot: 'Ajouter un créneau',
    timetable: 'Emploi du temps',
    cursus: 'Cursus',
    addCursusEntry: 'Ajouter une année',
    outcome: 'Résultat',
    notifications: 'Notifications',

    // Director / Cursus advanced
    director: 'Directeur',
    directorDashboard: 'Tableau de bord — Directeur des études',
    studentsScored: 'Élèves notés',
    classAverage: 'Moyenne classe',
    median: 'Médiane',
    passingRate: 'Taux de réussite',
    top3: 'Top 3',
    struggling: 'En difficulté (< 10/20)',
    noBulletinsForPeriod: 'Aucun bulletin sur cette période',
    noStrugglingStudents: 'Aucun élève en difficulté ✓',
    noBulletins: 'Aucun bulletin',
    syncToCursus: 'Synchroniser vers le cursus',
    syncedToCursus: 'Synchronisé vers le cursus',
    yearEndPromotion: 'Promotion de fin d\'année',
    yearEndPromotionDesc: 'Crée ou met à jour une entrée de cursus pour TOUS les élèves actifs.',
    defaultDecision: 'Décision par défaut',
    promoted: 'Admis (passé en classe sup.)',
    repeated: 'Redouble',
    transferred: 'Transféré',
    leftSchool: 'Quitté',
    applyToWholeClass: 'Appliquer à toute la classe',
    confirmPromote: 'Promouvoir tous les élèves de {class} pour {year} en "{outcome}" ?',
    studentsAffected: 'élève(s) traité(s)',
    cursusForClass: 'Cursus',
    noCursusEntries: 'Aucune entrée de cursus pour cette classe/année',
    pickClass: '— Choisir —',
    pickPeriod: 'Période',
    classLabel: 'Classe',
    decisionLabel: 'Décision',
    commentShort: 'Comment.',
    studentLabel: 'Élève',
    rankLabel: 'Rang',
    avgLabel: 'Moyenne',

    // Cantine
    cantineMenus: 'Menus cantine',
    newMenu: 'Nouveau menu',
    starter: 'Entrée',
    mainCourse: 'Plat principal',
    dessert: 'Dessert',
    notes: 'Notes',
    todayMenu: 'Menu du jour',
    upcomingMenus: 'Menus à venir (30j)',
    noScheduledMenu: 'Aucun menu programmé',
    menuSaved: 'Menu enregistré',
    deleteMenu: 'Supprimer ce menu ?',
    allergiesAndDiet: 'Allergies & régime',
    declaredAllergies: 'Allergies déclarées',
    noAllergies: 'Aucune',
    addAllergy: '+ Ajouter',
    allergyExample: 'Ex: arachides',
    dietNotes: 'Notes / régime',
    dietNotesPlaceholder: 'Végétarien, halal, sans porc…',
    cantineFees: 'Frais de cantine',
    paid: 'Payé',
    saved: 'Enregistré',
    checkAllergies: 'Vérifier les allergies',
    studentSearchPlaceholder: 'Tapez le nom de l\'élève…',
    newSearch: 'Nouvelle recherche',
    diet: 'Régime',
    noPublishedMenu: 'Aucun menu publié',

    // Transport
    transportRoutes: 'Circuits de transport',
    newRoute: 'Nouveau circuit',
    routeName: 'Nom',
    busPlate: 'Plaque',
    stops: 'Arrêts',
    stop: 'Arrêt',
    addStop: '+ Arrêt',
    subscribedStudents: 'Élèves abonnés',
    routeSaved: 'Circuit enregistré',
    deleteRoute: 'Supprimer ce circuit ?',
    noStops: 'Aucun arrêt configuré',
    noRoutes: 'Aucun circuit',
    noSubscribedStudents: 'Aucun élève abonné',
    busTracking: 'Suivi du bus',
    busPosition: 'Position du bus',
    live: 'Direct',
    offline: 'Hors ligne',
    minutesAgo: 'Il y a {n} min',
    waitingGps: 'En attente du signal GPS du chauffeur…',
    transportFees: 'Frais de transport',
    pickRoute: '— Choisir —',
    openInOSM: 'Ouvrir dans OpenStreetMap',

    // Mobile staff
    mobileLoginTitle: 'Connexion personnel terrain',
    mobileLoginSub: 'Cantine · Transport',
    staffId: 'Identifiant agent',
    pinCode: 'Code PIN (4 chiffres)',
    pinPlaceholder: 'Ex: 67ab12cd…',
    connecting: 'Connexion…',
    invalidCredentials: 'Identifiants invalides',
    pinAndIdRequired: 'Identifiant + PIN à 4 chiffres requis',
    pinFromAdmin: 'Demandez votre identifiant et votre PIN à l\'administration.',
    welcomeUser: 'Bienvenue',
    driver: 'Chauffeur',
    myRoute: 'Mon circuit',
    pickRouteShort: '— Sélectionner —',
    startGpsTracking: 'Démarrer le suivi GPS',
    stopTracking: 'Arrêter le suivi',
    trackingStarted: 'Suivi GPS démarré',
    trackingStopped: 'Suivi GPS arrêté',
    pickRouteFirst: 'Choisissez un circuit',
    geoNotSupported: 'Géolocalisation non supportée',
    lastPosition: 'Dernière position',
    waitingFirstFix: 'En attente du premier signal GPS…',
    qrSheet: 'Fiches QR',
    students_count: 'Élèves',
    periode1: 'Période 1',
    periode2: 'Période 2',
    periode3: 'Période 3',
    periode4: 'Période 4',
    periode5: 'Période 5',
    periode6: 'Période 6',
    periode7: 'Période 7',
    examenBlanc1: 'Examen Blanc 1',
    examenBlanc2: 'Examen Blanc 2',
    examenBlanc3: 'Examen Blanc 3',
    qrCheckin: 'Pointage QR',
    boardAction: 'Montée',
    alightAction: 'Descente',
    scanQrButton: 'Scanner un QR',
    unknownQr: 'QR inconnu',
    boarded: 'Monté',
    alighted: 'Descendu',
    canteenStaff: 'Cantine',
    canteenCheckinTitle: 'Pointage repas — aujourd\'hui',
    todayMenuShort: 'Menu du jour',
    lastPositionAt: 'Dernière position',
    cantineAllergyAlert: 'Allergies',
    cantineDietLabel: 'Régime',
  },
  en: {
    // Navigation
    dashboard: 'Dashboard',
    students: 'Students',
    teachers: 'Teachers',
    staff: 'Staff',
    payments: 'Payments',
    reports: 'Reports',
    bulletins: 'Report Cards',
    expenses: 'Expenses',
    supplies: 'Supplies',
    attendance: 'Attendance',
    salaries: 'Salaries',
    settings: 'Settings',
    users: 'Users',
    logout: 'Logout',
    
    // Auth
    login: 'Login',
    email: 'Email',
    password: 'Password',
    loginButton: 'Sign In',
    forgotPassword: 'Forgot password?',
    
    // Dashboard
    welcome: 'Welcome',
    totalStudents: 'Total Students',
    totalTeachers: 'Total Teachers',
    totalStaff: 'Staff',
    todayPayments: 'Today\'s Payments',
    monthPayments: 'Month\'s Payments',
    outstandingBalance: 'Outstanding Balance',
    
    // Students
    addStudent: 'Add Student',
    editStudent: 'Edit Student',
    firstName: 'First Name',
    lastName: 'Last Name',
    fatherName: 'Father\'s Name',
    motherName: 'Mother\'s Name',
    parentContact: 'Parent Contact',
    nationality: 'Nationality',
    address: 'Address',
    class: 'Class',
    cycle: 'Cycle',
    dateOfBirth: 'Date of Birth',
    importCSV: 'Import CSV',
    exportExcel: 'Export Excel',
    search: 'Search',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    
    // Payments
    addPayment: 'New Payment',
    paymentType: 'Payment Type',
    tuition: 'Tuition',
    canteen: 'Canteen',
    transport: 'Transport',
    extra: 'Extra',
    amount: 'Amount',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    cheque: 'Cheque',
    transfer: 'Transfer',
    deposit: 'Deposit',
    receipt: 'Receipt',
    printReceipt: 'Print Receipt',
    balance: 'Balance',
    
    // Reports
    financialReport: 'Financial Report',
    forecastReport: 'Forecast',
    actualReport: 'Actual',
    totalExpected: 'Total Expected',
    totalPaid: 'Total Paid',
    totalBalance: 'Total Balance',
    
    // Bulletins
    addBulletin: 'New Report Card',
    period: 'Period',
    trimester1: '1st Trimester',
    trimester2: '2nd Trimester',
    trimester3: '3rd Trimester',
    semester1: '1st Semester',
    semester2: '2nd Semester',
    schoolYear: 'School Year',
    subject: 'Subject',
    grade: 'Grade',
    average: 'Average',
    teacherComment: 'Teacher Comment',
    rank: 'Rank',
    downloadPDF: 'Download PDF',
    
    // Staff
    addTeacher: 'Add Teacher',
    addStaff: 'Add Staff',
    contact: 'Contact',
    salary: 'Salary',
    canteenStaff: 'Canteen Staff',
    transportStaff: 'Transport Staff',
    educators: 'Educators',
    cleaningStaff: 'Cleaning Staff',
    directionStaff: 'Management',
    pedagogicalGroup: 'Pedagogical',
    servicesGroup: 'Services',
    directionGroup: 'Management',
    function: 'Function',
    contractType: 'Contract Type',
    address: 'Address',
    birthdate: 'Date of Birth',
    hireDate: 'Hire Date',
    contractCDI: 'Permanent (CDI)',
    contractCDD: 'Fixed-term (CDD)',
    contractIntern: 'Intern',
    contractVacataire: 'Contractor',
    contractOther: 'Other',
    
    // Attendance
    markAttendance: 'Mark Attendance',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    date: 'Date',
    
    // Expenses
    addExpense: 'Add Expense',
    designation: 'Designation',
    schoolExpense: 'School Expense',
    canteenExpense: 'Canteen Expense',
    transportExpense: 'Transport Expense',
    
    // Supplies
    addSupply: 'Add Supply',
    quantity: 'Quantity',
    unitPriceSale: 'Unit Sale Price',
    unitPricePurchase: 'Unit Purchase Price',
    total: 'Total',
    
    // Users
    addUser: 'Add User',
    role: 'Role',
    admin: 'Administrator',
    director_general: 'General Director',
    comptable: 'Accountant',
    rh: 'Human Resources',
    director_studies: 'Director of Studies',
    cashier: 'Cashier',
    secretary: 'Secretariat',
    vigil: 'Security Guard',
    user: 'User',
    
    // Common
    confirm: 'Confirm',
    confirmDelete: 'Are you sure you want to delete?',
    success: 'Success',
    error: 'Error',
    loading: 'Loading...',
    noData: 'No data',
    all: 'All',
    filter: 'Filter',
    
    // Cycles
    maternelle: 'Kindergarten',
    primaire: 'Primary',
    college: 'Middle School',
    lycee: 'High School',
    
    // Months
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    
    // Parent Portal
    parentPortal: 'Parent Portal',
    myChildren: 'My Children',
    childBulletins: 'Report Cards',
    paymentHistory: 'Payment History',
    messages: 'Messages',
    sendMessage: 'Send Message',
    newMessage: 'New Message',
    inbox: 'Inbox',
    subject: 'Subject',
    message: 'Message',
    reply: 'Reply',
    viewDetails: 'View Details',
    parentRegister: 'Parent Registration',
    studentCode: 'Student Code',
    registerAsParent: 'Register as Parent',
    alreadyHaveAccount: 'Already have an account?',
    noAccount: 'Don\'t have an account?',
    totalDue: 'Total Due',
    amountPaid: 'Amount Paid',
    remainingBalance: 'Remaining Balance',
    phone: 'Phone',
    generateCode: 'Generate Parent Code',
    parentCode: 'Parent Code',
    copyCode: 'Copy Code',
    codeCopied: 'Code copied!',
    messageSchool: 'Contact School',
    unread: 'Unread',
    read: 'Read',
    replied: 'Replied',
    from: 'From',
    to: 'To',
    sentAt: 'Sent at',

    // School Settings & Appointments
    schoolSettings: 'School Settings',
    generalInfo: 'General Information',
    schoolYears: 'School Years',
    feeConfig: 'Fee Configuration',
    classFeeConfig: 'Class Fees',
    schoolName: 'School Name',
    founder: 'Founder',
    directorOfStudies: 'Director of Studies',
    logoUrl: 'Logo URL',
    addSchoolYear: 'Add School Year',
    startDate: 'Start Date',
    endDate: 'End Date',
    active: 'Active',
    setActive: 'Set Active',
    closeYear: 'Close',
    closedYear: 'Closed',
    fiscalArchives: 'Fiscal Archives',
    registrationFee: 'Registration Fee',
    reregistrationFee: 'Re-registration Fee',
    transportZones: 'Transport Zones',
    addZone: 'Add Zone',
    zoneName: 'Zone Name',
    zoneAmount: 'Amount',
    appointments: 'Appointments',
    bookAppointment: 'Book Appointment',
    teacher: 'Teacher',
    selectTeacher: 'Select a teacher',
    selectStudent: 'Select a student',
    selectDate: 'Pick a date',
    selectSlot: 'Pick a slot',
    reason: 'Reason',
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
    confirmAppointment: 'Confirm',
    cancelAppointment: 'Cancel',
    completeAppointment: 'Mark Completed',
    setAvailability: 'Set Availability',
    myAvailability: 'My Availability',
    dayMonday: 'Monday',
    dayTuesday: 'Tuesday',
    dayWednesday: 'Wednesday',
    dayThursday: 'Thursday',
    dayFriday: 'Friday',
    daySaturday: 'Saturday',
    daySunday: 'Sunday',
    startTime: 'Start Time',
    endTime: 'End Time',
    slotDuration: 'Duration (min)',
    noSlots: 'No slots available',
    appointmentBooked: 'Appointment booked',
    saveSettings: 'Save',

    // Timetable
    timetables: 'Timetables',
    addSlot: 'Add slot',
    timetable: 'Timetable',
    cursus: 'Cursus',
    addCursusEntry: 'Add a year',
    outcome: 'Outcome',
    notifications: 'Notifications',

    // Director / Cursus advanced
    director: 'Director',
    directorDashboard: 'Director of Studies — Dashboard',
    studentsScored: 'Students with grades',
    classAverage: 'Class average',
    median: 'Median',
    passingRate: 'Pass rate',
    top3: 'Top 3',
    struggling: 'Struggling (< 10/20)',
    noBulletinsForPeriod: 'No report cards for this period',
    noStrugglingStudents: 'No struggling students ✓',
    noBulletins: 'No report cards',
    syncToCursus: 'Sync to cursus',
    syncedToCursus: 'Synced to cursus',
    yearEndPromotion: 'Year-end promotion',
    yearEndPromotionDesc: 'Creates or updates a cursus entry for ALL active students.',
    defaultDecision: 'Default decision',
    promoted: 'Promoted (next grade)',
    repeated: 'Repeated',
    transferred: 'Transferred',
    leftSchool: 'Left',
    applyToWholeClass: 'Apply to entire class',
    confirmPromote: 'Promote all {class} students for {year} as "{outcome}"?',
    studentsAffected: 'student(s) processed',
    cursusForClass: 'Cursus',
    noCursusEntries: 'No cursus entries for this class/year',
    pickClass: '— Pick one —',
    pickPeriod: 'Period',
    classLabel: 'Class',
    decisionLabel: 'Decision',
    commentShort: 'Comment',
    studentLabel: 'Student',
    rankLabel: 'Rank',
    avgLabel: 'Average',

    // Cantine
    cantineMenus: 'Canteen menus',
    newMenu: 'New menu',
    starter: 'Starter',
    mainCourse: 'Main course',
    dessert: 'Dessert',
    notes: 'Notes',
    todayMenu: 'Today\'s menu',
    upcomingMenus: 'Upcoming menus (30d)',
    noScheduledMenu: 'No scheduled menu',
    menuSaved: 'Menu saved',
    deleteMenu: 'Delete this menu?',
    allergiesAndDiet: 'Allergies & diet',
    declaredAllergies: 'Declared allergies',
    noAllergies: 'None',
    addAllergy: '+ Add',
    allergyExample: 'e.g. peanuts',
    dietNotes: 'Notes / diet',
    dietNotesPlaceholder: 'Vegetarian, halal, no pork…',
    cantineFees: 'Canteen fees',
    paid: 'Paid',
    saved: 'Saved',
    checkAllergies: 'Check allergies',
    studentSearchPlaceholder: 'Type a student\'s name…',
    newSearch: 'New search',
    diet: 'Diet',
    noPublishedMenu: 'No menu published',

    // Transport
    transportRoutes: 'Transport routes',
    newRoute: 'New route',
    routeName: 'Name',
    busPlate: 'Plate',
    stops: 'Stops',
    stop: 'Stop',
    addStop: '+ Stop',
    subscribedStudents: 'Subscribed students',
    routeSaved: 'Route saved',
    deleteRoute: 'Delete this route?',
    noStops: 'No stops configured',
    noRoutes: 'No routes',
    noSubscribedStudents: 'No subscribed students',
    busTracking: 'Bus tracking',
    busPosition: 'Bus position',
    live: 'Live',
    offline: 'Offline',
    minutesAgo: '{n} min ago',
    waitingGps: 'Waiting for the driver\'s GPS signal…',
    transportFees: 'Transport fees',
    pickRoute: '— Pick one —',
    openInOSM: 'Open in OpenStreetMap',

    // Mobile staff
    mobileLoginTitle: 'Field staff login',
    mobileLoginSub: 'Canteen · Transport',
    staffId: 'Agent ID',
    pinCode: 'PIN code (4 digits)',
    pinPlaceholder: 'e.g. 67ab12cd…',
    connecting: 'Signing in…',
    invalidCredentials: 'Invalid credentials',
    pinAndIdRequired: 'Agent ID + 4-digit PIN required',
    pinFromAdmin: 'Ask the office for your ID and PIN.',
    welcomeUser: 'Welcome',
    driver: 'Driver',
    myRoute: 'My route',
    pickRouteShort: '— Select —',
    startGpsTracking: 'Start GPS tracking',
    stopTracking: 'Stop tracking',
    trackingStarted: 'GPS tracking started',
    trackingStopped: 'GPS tracking stopped',
    pickRouteFirst: 'Pick a route first',
    geoNotSupported: 'Geolocation not supported',
    lastPosition: 'Last position',
    waitingFirstFix: 'Waiting for first GPS fix…',
    qrSheet: 'QR Sheet',
    students_count: 'Students',
    periode1: 'Period 1',
    periode2: 'Period 2',
    periode3: 'Period 3',
    periode4: 'Period 4',
    periode5: 'Period 5',
    periode6: 'Period 6',
    periode7: 'Period 7',
    examenBlanc1: 'Mock Exam 1',
    examenBlanc2: 'Mock Exam 2',
    examenBlanc3: 'Mock Exam 3',
    qrCheckin: 'QR check-in',
    boardAction: 'Board',
    alightAction: 'Alight',
    scanQrButton: 'Scan a QR',
    unknownQr: 'Unknown QR',
    boarded: 'Boarded',
    alighted: 'Alighted',
    canteenStaff: 'Canteen',
    canteenCheckinTitle: 'Meal check-in — today',
    todayMenuShort: 'Today\'s menu',
    lastPositionAt: 'Last position',
    cantineAllergyAlert: 'Allergies',
    cantineDietLabel: 'Diet',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    translations: translations[language]
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageContext;
